import { Box, Checkbox, duration, FormControlLabel, Grid, Paper, TextField, Tooltip } from "@mui/material";
import RichTextEditor from "../../../components/rich-text/rich-text-editor";
import DropdownSection from "../../../../../components/dropdown-section";
import { Assessment } from "../../../../../models/assessment";
import StarIcon from "../../assets/icons/star.png";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useClassroomActions } from "../../../../../hooks/useClassroomActions";

interface SettingsInterface {
    setSaved: React.Dispatch<React.SetStateAction<boolean>>;
    assessment: Assessment;
}

function Settings({setSaved, assessment }: SettingsInterface) {

    const [locAssessment, setLocAssessment] = useState<Assessment>(assessment);

    const { updateAssessment } = useClassroomActions();
    
    const autoSaveTimeout = useRef<NodeJS.Timeout | null>(null);

    const debouncedAutoSave = (ass: Assessment) => {
        if (autoSaveTimeout.current) clearTimeout(autoSaveTimeout.current);

        autoSaveTimeout.current = setTimeout( async () => {
            
            await updateAssessment(assessment.classroom_id, ass);
            setSaved(true);
        }, 3000);
    };

    const handleAssessmentChange = (field: keyof Assessment, value: any) => {
        setSaved(false);
        
        const newAssessment = {
            ...locAssessment,
            [field]: value
        }
        setLocAssessment(newAssessment);

        debouncedAutoSave(newAssessment);
    };

    useEffect(() => {
        setLocAssessment(assessment);
    }, [assessment]);

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <Paper sx={{ margin: 'auto', maxWidth: '1000px', padding: 3, }}>
                <TextField
                    fullWidth
                    label="Title"
                    variant="outlined"
                    value={locAssessment.title}
                    onChange={(e) => handleAssessmentChange("title", e.target.value)}
                    sx={{ marginBottom: 2 }}
                />

                <RichTextEditor
                    id={String(locAssessment.id)}
                    type="description"
                    value={locAssessment.instruction}
                    onChange={(val) => handleAssessmentChange("instruction", val)}
                    userPlaceholder="Enter optional Instructions"
                />

                <DropdownSection title="Timer Settings">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={locAssessment.duration > 0}
                                onChange={(e) =>
                                    handleAssessmentChange("duration", e.target.checked ? 1 : 0)
                                }
                            />
                        }
                        label="Enable Timer"
                    />
                    {locAssessment.duration > -1 && (
                        <Box sx={{ ml: 4, mt: 1 }}>
                            <TextField
                                type="number"
                                label="Duration (minutes)"
                                fullWidth
                                value={locAssessment.duration}
                                onChange={(e) =>
                                    handleAssessmentChange("duration", e.target.value)
                                }
                                sx={{ marginBottom: 3 }}
                            />
                        </Box>
                    )}
                </DropdownSection>

                <DropdownSection title="Auto Marking">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={locAssessment.auto_marker}
                                onChange={(e) => handleAssessmentChange("auto_marker", e.target.checked)}
                            />
                        }
                        label={
                            <Box display="flex" alignItems="center">
                                <span>Enable Auto Marker</span>
                                <Tooltip title="Powered by AI">
                                    <Box
                                        component="img"
                                        src={StarIcon}
                                        alt="AI"
                                        sx={{ width: 20, height: 20, ml: 1 }}
                                    />
                                </Tooltip>
                            </Box>
                        }
                    />

                    {locAssessment.auto_marker && (
                        <Box sx={{ ml: 4, mt: 1, display: 'flex', flexDirection: 'column' }}>

                            <TextField
                                variant="standard"
                                value={locAssessment.default_marks}
                                onChange={(e) => handleAssessmentChange("default_marks", e.target.value)}
                                label="Default Marks"
                                type="number"
                                sx={{ width: '100px'}}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={locAssessment.show_score}
                                        onChange={(e) =>
                                            handleAssessmentChange("show_score", e.target.checked)
                                        }
                                    />
                                }
                                label="Show Score After Submission"
                                sx={{ display: 'block', mt: 1 }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={locAssessment.show_summary}
                                        disabled
                                        onChange={(e) =>
                                            handleAssessmentChange("show_summary", e.target.checked)
                                        }
                                    />
                                }
                                label="Show Summary"
                                sx={{ display: 'block', mt: 1 }}
                            />
                        </Box>
                    )}
                </DropdownSection>

                <DropdownSection title="Miscellaneous Settings">
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={locAssessment.shuffle_questions}
                                        onChange={(e) =>
                                            handleAssessmentChange("shuffle_questions", e.target.checked)
                                        }
                                    />
                                }
                                label="Shuffle Questions Per Section"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={locAssessment.multiple_submissions}
                                        onChange={(e) =>
                                            handleAssessmentChange("multiple_submissions", e.target.checked)
                                        }
                                    />
                                }
                                label="Allow Multiple Submissions"
                            />
                        </Grid>
                    </Grid>
                </DropdownSection>
            </Paper>
        </Box>
    );
}

export default memo(Settings);